import { LoadingButton } from "@mui/lab";
import { Button, Grid, Paper, TextField } from "@mui/material";
import gridStateToPayload from "modules/shop_grid/gridStateToPayload";
import ShopGridView from "modules/shop_grid/ShopGridView";
import useShopGridController from "modules/shop_grid/useShopGridController";
import { deleteShopGrid, enableShopGrid, useShopGridUpdate } from "queries/shop_grid";
import { Api } from "queries/types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import CellInventoryForm from "../inventory/CellInventoryForm";
import LegendModal from "../inventory/LegendModal";
import { Feature, useAuthorization } from "user";
import useAlert from "components/useAlert";
import { useNavigate } from "react-router-dom";


interface AutoSaveNameFieldProps {
  gridId: number;
  gridName: string;
}

const AutoSaveNameField = ({ gridId, gridName }: AutoSaveNameFieldProps) => {
  const updateMutation = useShopGridUpdate();
  const [name, setName] = useState(gridName);
  const { setAlert } = useAlert();
  const { t } = useTranslation();
  const isError = name.length === 0;

  return (
    <TextField
      fullWidth
      value={name}
      onChange={(e) => setName(e.target.value)}
      label={t("shop.inventory_proposal.name")}
      variant="standard"
      error={isError}
      helperText={isError && t("shop.inventory_proposal.nameError")}
      onBlur={() => {
        const trimmedName = name.trim();
        trimmedName !== gridName && updateMutation.mutate({ id: gridId, name: trimmedName }, {
          onError: (error: any) => setAlert(error?.response?.data?.detail, 'error')
        });
        setName(trimmedName);
      }}
    />
  );
};

interface ShopInventoryProposalProps {
  activeGrid: Api.ShopGrid;
  families: Api.Family[];
  gridProposal: Api.ShopGrid;
  onProposalApplied?: () => void;
  disableCellEdition?: Boolean;
  comparatorCellEdition?: Boolean;
}

const ShopInventoryProposal = ({
  activeGrid,
  families,
  gridProposal,
  onProposalApplied,
  disableCellEdition = false,
  comparatorCellEdition = false,
}: ShopInventoryProposalProps) => {
  const { grid, selectedCells, toggleCell, unselectAll, configureCells, gridFurnitures, gridFamilies } =
    useShopGridController(activeGrid, { families, gridProposal });
  const updateMutation = useShopGridUpdate();
  const hasAccess = useAuthorization();
  const { setAlert } = useAlert();
  const navigate = useNavigate();
  const [showLegend, setShowLegend] = useState(false);
  const { t } = useTranslation();

  return (
    <Grid container direction="column" sx={{ position: "absolute", left: 0, width: "100%" }}>
      <Grid item xs={12} sx={{ maxWidth: "100% !important" }}>
        <Paper sx={{ width: "100%", boxSizing: "border-box", p: { xs: 1, sm: 2 } }}>
          <Grid container flexWrap="nowrap" direction="column">
            <Grid container>
              {
                !comparatorCellEdition && 
                <Grid item xs={3}>
                  <AutoSaveNameField gridId={gridProposal.id} gridName={gridProposal.name} />
                </Grid>
              }
              <Grid item alignSelf="flex-end">
                <Button variant="text" color="info" onClick={() => setShowLegend(true)}>
                  {t("shop.inventory.legend.display")}
                </Button>
              </Grid>
            </Grid>
            {showLegend && (
              <LegendModal furnitures={gridFurnitures} families={gridFamilies} onClose={() => setShowLegend(false)} />
            )}
            <Grid item sx={{ mt: 2, overflow: "auto" }} xs={12}>
              <ShopGridView
                grid={grid}
                selectedCells={selectedCells}
                toggleCell={toggleCell}
                mode="inventory"
                maxHeight="70vh"
                showDiff
                modeCell={comparatorCellEdition ? "comparator" : undefined}
              />
            </Grid>
            {selectedCells.length > 0 && !disableCellEdition ? (
              <Grid item xs={12} key={selectedCells.map((cell) => `${cell.yAxis}-${cell.xAxis}`).join()}>
                <CellInventoryForm
                  cells={selectedCells}
                  families={families}
                  onSubmit={(sections) => {
                    configureCells({ sections });
                    unselectAll();
                  }}
                  unselectAll={unselectAll}
                />
              </Grid>
            ) : ( onProposalApplied &&
              <Grid container >
                {hasAccess(Feature.DeleteProposal) &&
                  <Grid item alignSelf="flex-start" mt={4} xs={6}>
                    <LoadingButton
                      variant="contained"
                      onClick={async () => {
                        if (!window.confirm(t("shop.inventory_proposal.delete", { name: gridProposal.name })))
                          return;

                        deleteShopGrid(gridProposal.id)
                          .then(() => {
                            navigate("/")
                          }).catch((status) => {
                            setAlert(status?.response?.data?.detail, 'error');
                          });
                      }}
                      loading={updateMutation.isLoading}
                    >
                      {t("shop.inventory_proposal.delete")}
                    </LoadingButton>
                  </Grid>
                }
                {hasAccess(Feature.ApplyProposal) &&
                  <Grid item display="flex" justifyContent='flex-end' mt={4} xs={6}>
                    <LoadingButton
                      variant="contained"
                      onClick={async () => {
                        if (!window.confirm(t("shop.inventory_proposal.applyConfirmation", { name: gridProposal.name })))
                          return;
                        await updateMutation.mutateAsync(gridStateToPayload(grid, gridProposal.id, false));
                        await enableShopGrid(gridProposal.id);
                        onProposalApplied();
                      }}
                      loading={updateMutation.isLoading}
                    >
                      {t("shop.inventory_proposal.apply")}
                    </LoadingButton>
                  </Grid>
                }
              </Grid>
            )}
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default ShopInventoryProposal;
